import { useMemo } from "react";

import { SnippetAnswer } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";
import { UnlockBadge } from "app/components/React/QuickSearch/UnlockBadge/UnlockBadge";

import { buildSummaryExcerptAroundQuery } from "app/components/React/QuickSearch/helpers/buildSummaryExcerptAroundQuery";
import { splitTextBySearchQuery } from "app/components/React/QuickSearch/helpers/splitTextBySearchQuery";

import {
    IconFrame,
    LockedReport,
    ReportRowAnchor,
    ReportRowButton,
    SummaryLine,
    SummaryPartText,
} from "./QuickSearchReportRow.styles";

const { carbon } = colorsPalettes;

type QuickSearchReportRowProps = {
    title: string;
    description: string;
    onClick: () => void;
    dataAutomation: string;
    summarySnippet?: string;
    highlightQuery?: string;
    /** Destination of the row when it points to an external page (e.g. account links). */
    href?: string;
    /** Renders the row as a real anchor that opens the page in a new browser tab. */
    isExternal?: boolean;
    isLocked?: boolean;
};

export const QuickSearchReportRow = ({
    title,
    description,
    onClick,
    dataAutomation,
    summarySnippet,
    highlightQuery,
    href,
    isExternal,
    isLocked,
}: QuickSearchReportRowProps) => {
    const descriptionParts = useMemo(() => {
        if (!description) {
            return null;
        }
        return highlightQuery?.trim()
            ? splitTextBySearchQuery(description, highlightQuery)
            : [{ text: description, highlighted: false }];
    }, [description, highlightQuery]);

    const summaryExcerpt = useMemo(() => {
        if (!summarySnippet || !highlightQuery?.trim()) {
            return null;
        }
        return buildSummaryExcerptAroundQuery(summarySnippet, highlightQuery);
    }, [summarySnippet, highlightQuery]);

    const summaryParts =
        summaryExcerpt && highlightQuery?.trim()
            ? splitTextBySearchQuery(summaryExcerpt, highlightQuery)
            : summarySnippet && !highlightQuery?.trim()
              ? [{ text: summarySnippet, highlighted: false }]
              : null;

    const rowContent = (
        <>
            <IconFrame>
                <SnippetAnswer
                    size="xs"
                    color={carbon[400]}
                />
            </IconFrame>

            <Stack
                gap="4px"
                alignItems="flex-start"
                flex="1"
                minWidth={0}
            >
                <Flex
                    gap="4px"
                    alignItems="center"
                    flexWrap="wrap"
                    width="100%"
                >
                    <Text
                        fontVariant="chip1"
                        color={carbon[500]}
                    >
                        {title}
                    </Text>

                    {descriptionParts ? (
                        <span>
                            {descriptionParts.map((part, index) => (
                                <SummaryPartText
                                    key={`description-part-${index}`}
                                    as="span"
                                    fontVariant="queryBar1"
                                    $highlighted={part.highlighted}
                                >
                                    {part.text}
                                </SummaryPartText>
                            ))}
                        </span>
                    ) : null}

                    {isLocked ? (
                        <LockedReport>
                            <UnlockBadge />
                        </LockedReport>
                    ) : null}
                </Flex>

                {summaryParts ? (
                    <SummaryLine>
                        {summaryParts.map((part, index) => (
                            <SummaryPartText
                                key={`summary-part-${index}`}
                                as="span"
                                fontVariant="queryBar1"
                                $highlighted={part.highlighted}
                            >
                                {part.text}
                            </SummaryPartText>
                        ))}
                    </SummaryLine>
                ) : null}
            </Stack>
        </>
    );

    if (isExternal && href && !isLocked) {
        return (
            <ReportRowAnchor
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
                data-automation={dataAutomation}
            >
                {rowContent}
            </ReportRowAnchor>
        );
    }

    return (
        <ReportRowButton
            type="button"
            onClick={onClick}
            data-automation={dataAutomation}
        >
            {rowContent}
        </ReportRowButton>
    );
};
